import OfferDetails from "@/(private)/offers/components/offer-details";
import { useOffer } from "@/hooks/offers/offers";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { acceptOffers, rejectOffers } from "@/data/offers";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useCallback, useState, useEffect } from "react";
import { useInfoDialog } from "@/hooks/useInfoDialog";
import { Offers } from "@/types/offers";
import InfoDialog from "@/(private)/components/info-dialog";
import { Button } from "@repo/ui/components/ui/button";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useNotificationToggle } from "@repo/next-shared/components/notifications/notifications-icon";
import { useNotificationListToggle } from "@repo/next-shared/components/notifications/notifications-list";
import { Skeleton } from "@repo/ui/components/ui/skeleton";

export default function OfferNotification({ offerId }: { offerId: string }) {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const statusDialog = useInfoDialog();
  const [dialogAction, setDialogAction] = useState<"accept" | "reject">();
  const { data: offer, isLoading: offerLoading } = useQuery<Offers>(
    useOffer(offerId),
  );
  const toggleContext = useNotificationToggle();
  const notificationListToggleContext = useNotificationListToggle();

  const acceptMutation = useMutation({
    mutationFn: acceptOffers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers"] });
      statusDialog.openDialog();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: rejectOffers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers"] });
      statusDialog.openDialog();
    },
  });

  const confirmDialog = useConfirmDialog({
    onConfirm: () => {
      if (dialogAction === "accept") {
        acceptMutation.mutate([offer.id]);
      } else if (dialogAction === "reject") {
        rejectMutation.mutate([offer.id]);
      }
      //notificationListToggleContext?.setIsToggled(false);
    },
  });

  const handleAction = useCallback(
    (action: "accept" | "reject") => {
      setDialogAction(action);
      confirmDialog.openDialog();
    },
    [confirmDialog],
  );

  return (
    <>
      {offerLoading ? (
        <div>
          <Skeleton className="w-60 h-10" />
          <Skeleton className="w-[460px] max-md:w-full rounded-[20px] h-8 mt-4" />
          <div className="flex max-md:flex-col gap-5 mt-4">
            <Skeleton className="w-[200px] h-[200px] max-md:w-full" />
            <div className="w-full">
              <Skeleton className="w-full h-12" />
              <Skeleton className="w-20 h-4 mt-4" />
              <Skeleton className="w-20 h-4 mt-4" />
            </div>
          </div>
          <Skeleton className="w-40 h-8 mt-10" />
          <div className="flex max-md:flex-col gap-5 mt-4">
            <Skeleton className="w-40 h-40" />
            <Skeleton className="w-40 h-40" />
            <Skeleton className="w-40 h-40" />
            <Skeleton className="w-40 h-40" />
            <Skeleton className="w-40 h-40" />
            <Skeleton className="w-40 h-40" />
          </div>
        </div>
      ) : offer ? (
        <OfferDetails
          offer={offer}
          onAccept={() => handleAction("accept")}
          onReject={() => handleAction("reject")}
        ></OfferDetails>
      ) : (
        <div>
          <h3>
            {t("Notifications.notification-offer.offer-details-no-data-title")}
          </h3>
          <p className="mt-5">
            {t("Notifications.notification-offer.offer-details-no-data")}
          </p>
        </div>
      )}
      <InfoDialog
        open={confirmDialog.open}
        onClose={confirmDialog.closeDialog}
        title={
          dialogAction === "accept"
            ? t("offers.title_accept")
            : t("offers.title_reject")
        }
        description={t("offers.info_confirm")}
        actions={
          <Button className="text-base" onClick={confirmDialog.confirm}>
            {t("offers.cta_confirm")}
          </Button>
        }
      />

      <InfoDialog
        open={statusDialog.open}
        onClose={() => {
          statusDialog.closeDialog();
          notificationListToggleContext?.setIsToggled(false);
          toggleContext?.setIsToggled(false);
        }}
        title={t("offers.operation_completed")}
        description={dialogAction === "accept" ? t("offers.info_status") : ""}
        actions={
          <Link
            href={dialogAction === "accept" ? "/orders/open" : "/offers/open"}
          >
            <Button
              className="text-base"
              onClick={() => {
                statusDialog.closeDialog();
                notificationListToggleContext?.setIsToggled(false);
                toggleContext?.setIsToggled(false);
              }}
            >
              {dialogAction === "accept"
                ? t("offers.go_to_orders")
                : t("offers.go_to_offers")}
            </Button>
          </Link>
        }
      />
    </>
  );
}
